import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  GroupedPanelList,
  IconButton,
  Item,
  SearchField,
  Dialog,
  FileUploadField,
  FooterAction,
  enqueueToaster,
} from "@deere/isg.component-library";
import { IconAdd, IconChevronRight, IconUpload } from "@deere/icons/new";
import { hasPermission } from "../../../permissions";
import { AppContext } from "../../context/AppContext";
import { scaleGeoPointsToSvg, scaleRawCoordsToSvg } from "../utils/scaleGeoPoints";
import { resolveTaskLocations, findAttributeByUUID } from "../utils/resolveTaskLocations";
import "./MissionsSubpanel.css";

// const backendURI = '18.219.96.136/API';
const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

interface PolylineProps {
  points: string; // A string representing the points of the polyline
  stroke?: string; // Optional stroke color for the line
  strokeWidth?: number; // Optional stroke width for the line
}

const Polyline: React.FC<PolylineProps> = ({
  points,
  stroke = "black", // Default stroke color
  strokeWidth = 2, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polyline points={points} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};
// curMissionObject={curMissionObject} setCurMissionObject={setCurMissionObject}
function MissionsSubPanel({
  orgDataSession,
  currentTopicString,
  curMissionObject,
  setCurMissionObject,
  setCurMission,
  orgData,
  setCurrent,
  subPanel,
}: {
  orgDataSession: any;
  currentTopicString: any;
  curMissionObject: any;
  setCurMissionObject: (object: any) => void;
  setCurMission: (item: Item) => void;
  orgData: any;
  setCurrent: (item: Item, i: number) => void;
  subPanel: string;
}) {
  const { user } = React.useContext(AppContext);
  const role = user.role;
  const [missions, setMissions] = useState<Item[]>([]);
  const [activeItem, setActiveItem] = useState(-1); // Highlighted item in list

  useEffect(() => {
    if (subPanel === "empty") {
      setActiveItem(-1);
    }
  }, [subPanel]);

  const [filteredMissions, setFilteredMissions] = useState<Item[]>([]);
  const [showMissionPanel, setShowMissionPanel] = useState(false);
  const [selectedMission, setSelectedMission] = useState<Item | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [missionsObjects, setMissionsObjects] = useState<any>();
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<any>(null);
  const [importWarnings, setImportWarnings] = useState<any[]>([]);
  const [totalNavigateTasks, setTotalNavigateTasks] = useState(0);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateImportData, setDuplicateImportData] = useState<any>(null);
  const [duplicateMissionName, setDuplicateMissionName] = useState("");

  function getMissionFromID(title: string): any | null {
    // Retrieve the missions from orgData using the existing getMissions function
    const missionsObject = getMissions();

    // Access the Missions object
    const missions = (missionsObject as any).Missions;

    // Search for the mission with the specified ID
    if (missions) {
      for (const missionId in missions) {
        const mission = missions[missionId];
        // console.log("MISSION ID: ", missionId);
        // console.log("Title: ", title);
        if (missionId === title) {
          // Compare missionId with the given ID
          return mission; // Return the found mission object
        }
      }
    }

    return null; // Return null if no mission is found with the given ID
  }

  function getMissions(): object {
    // Recursive function to search for Missions in the object
    const findMissions = (obj: any): object | null => {
      // Check if the current object has a Missions key
      if (obj && typeof obj === "object") {
        if ("Missions" in obj) {
          return obj.Missions; // Return the Missions object if found
        }

        // Recursively search through each property in the object
        for (const key in obj) {
          const result = findMissions(obj[key]);
          if (result) {
            return result; // Return the result if Missions is found in the current branch
          }
        }
      }
      return null; // Return null if Missions is not found
    };

    const missions = findMissions(orgDataSession);
    return missions ? { Missions: missions } : { Missions: {} }; // Return found missions or an empty object
  }

  function getMissionItem(missionObject: any): {
    id: string;
    title: string;
    subtitle: string;
    icon: JSX.Element;
  } {
    // Extract the necessary properties from the mission object
    // console.log(Object.values(missionObject)[0]);
    // console.log("Mission Key: ", Object.keys(missionObject)[0]);
    let mission: any = Object.values(missionObject)[0];
    const missionId = (Object.keys(missionObject)[0] as string) || "0"; // Use vehicleID or default to '0'
    const title = mission.missionName || "Unknown Mission";
    const subtitle = mission.missionDescription || "No description available";

    // Collect all route points from tasks into a flat array for uniform-scale projection
    const allRoutePoints: any[] = [];
    mission.Tasks &&
      Object.values(mission.Tasks).forEach((task) => {
        (task as any).route.points.forEach((point: any) => {
          allRoutePoints.push(point);
        });
      });

    // Use shared uniform-scale utility to preserve aspect ratio
    const points = scaleGeoPointsToSvg(allRoutePoints);

    // Create the mission item object
    const missionItem = {
      id: missionId,
      title: title,
      subtitle: subtitle,
      icon: <Polyline points={points} stroke="#3290ff" strokeWidth={4} />, // Use points to draw the Polyline
    };

    // console.log(points);

    return missionItem;
  }

  function getMissionItem2(
    mission: any,
    missionId: string,
  ): { id: string; title: string; subtitle: string; icon: JSX.Element } {
    // Extract the necessary properties from the mission object
    // console.log(mission);
    // console.log("Mission Key: ", missionId);
    // let mission: any = Object.values(missionObject)[0];
    // const missionId = (Object.keys(missionObject)[0] as string) || '0'; // Use vehicleID or default to '0'
    const title = mission.missionName || "Unknown Mission";
    const subtitle = mission.missionDescription || "No description available";

    // Get original bounds for scaling
    const originalLatValues: any = [];
    const originalLngValues: any = [];
    // console.log("mission", mission);
    // mission.Tasks && Object.values(mission.Tasks).forEach(task => {
    //     (task as any).route.points.forEach((point: any) => {
    //         originalLatValues.push(point.location.latitude);
    //         originalLngValues.push(point.location.longitude);
    //     });
    // });
    // console.log("mission", mission);

    if (mission.tasks) {
      // Resolve UUID-only tasks to full location data from jobsite attributes
      const resolvedTasks = resolveTaskLocations(mission.tasks, orgDataSession);
      // Collect all points from each task
      resolvedTasks.forEach((task: any) => {
        if (task.navigateItem) {
          // Assuming task.navigateItem is where the selected item is stored
          if (task?.action === "Navigate") {
            if (task.properties.location) {
              // allPoints.push({ location: task.properties.location });
              originalLatValues.push(task.properties.location.latitude);
              originalLngValues.push(task.properties.location.longitude);
            } else if (task.properties.zoneType !== undefined) {
              const points = task.properties.points;
              let latSum = 0;
              let lngSum = 0;
              points.forEach((point: any) => {
                latSum += point.location.latitude;
                lngSum += point.location.longitude;
              });
              originalLatValues.push(latSum / points.length);
              originalLngValues.push(lngSum / points.length);
            } else {
              const points = task.properties.points;
              points.forEach((point: any) => {
                // allPoints.push(point);
                originalLatValues.push(point.location.latitude);
                originalLngValues.push(point.location.longitude);
              });
            }
          }
        }
      });
      // Adding the first Task in the list again for visualizing repetition
      if (resolvedTasks.length > 0 && resolvedTasks[0].navigateItem) {
        // Assuming task.navigateItem is where the selected item is stored
        if (resolvedTasks[0]?.action === "Navigate") {
          if (resolvedTasks[0].properties.location) {
            // allPoints.push({ location: resolvedTasks[0].properties.location })
            originalLatValues.push(resolvedTasks[0].properties.location.latitude);
            originalLngValues.push(resolvedTasks[0].properties.location.longitude);
          } else if (resolvedTasks[0].properties.zoneType !== undefined) {
            const points = resolvedTasks[0].properties.points;
            let latSum = 0;
            let lngSum = 0;
            points.forEach((point: any) => {
              latSum += point.location.latitude;
              lngSum += point.location.longitude;
            });
            originalLatValues.push(latSum / points.length);
            originalLngValues.push(lngSum / points.length);
          } else {
            const points = resolvedTasks[0].properties.points;
            points.forEach((point: any) => {
              // allPoints.push(point);
              originalLatValues.push(point.location.latitude);
              originalLngValues.push(point.location.longitude);
            });
          }
        }
      }
    }

    // if (mission.hasOwnProperty("guidanceRoute")) {

    //     mission.guidanceRoute.points.forEach((point: any) => {
    //         originalLatValues.push(point.location.latitude);
    //         originalLngValues.push(point.location.longitude);
    //     });
    // }
    // console.log(originalLatValues);

    // Use shared uniform-scale utility to preserve aspect ratio
    const points = scaleRawCoordsToSvg(originalLatValues, originalLngValues);

    // Create the mission item object
    const missionItem = {
      id: missionId,
      title: title,
      subtitle: subtitle,
      icon: <Polyline points={points} stroke="#3290ff" strokeWidth={4} />, // Use points to draw the Polyline
    };

    // console.log(points);

    return missionItem;
  }

  useEffect(() => {
    let theseMissions = getMissions();
    // console.log(theseMissions);

    let myMissions: Item[] = [];
    // console.log(missionsObjects["Missions"]);
    if (theseMissions) {
      let newMissionsObject: any = Object.values(theseMissions)[0];
      if (newMissionsObject) {
        // Sort by mission key so row order is stable across full-state dataUpdates.
        const sortedKeys = Object.keys(newMissionsObject).sort((a, b) => a.localeCompare(b));
        for (const missionKey of sortedKeys) {
          myMissions.push(
            getMissionItem2(newMissionsObject[missionKey], missionKey),
          );
        }
      }
    }
    // for (const missionIndex in missionsObjects) {
    //     console.log(missionsObjects[missionIndex]);
    //     console.log(Object.values(missionsObjects)[0]);
    //     myMissions.push(getMissionItem(missionsObjects[missionIndex]));
    // }
    // console.log(myMissions);
    setMissionsObjects(theseMissions);
    setMissions(myMissions);
    setFilteredMissions(myMissions);
  }, [orgDataSession]);

  // useEffect(() => {
  //     let myMissions: Item[] = [];
  //     // console.log(missionsObjects["Missions"]);
  //     if (missionsObjects) {
  //         let newMissionsObject: any = Object.values(missionsObjects)[0];
  //         if (newMissionsObject) {
  //             // console.log(Object.values(missionsObjects)[0]);
  //             for (const missionIndex in Object.values(newMissionsObject)) {
  //                 // console.log(newMissionsObject);
  //                 // console.log(missionsObjects[missionIndex]);
  //                 myMissions.push(getMissionItem2(Object.values(newMissionsObject)[missionIndex], Object.keys(newMissionsObject)[missionIndex] as string));
  //             }
  //         }
  //     }
  //     // for (const missionIndex in missionsObjects) {
  //     //     console.log(missionsObjects[missionIndex]);
  //     //     console.log(Object.values(missionsObjects)[0]);
  //     //     myMissions.push(getMissionItem(missionsObjects[missionIndex]));
  //     // }
  //     // console.log(myMissions);
  //     setMissions(myMissions);
  //     setFilteredMissions(myMissions);
  // }, [missionsObjects])

  // useEffect(() => {
  //     const initialMissions: Item[] = [
  //         { id: '0', title: 'Mission 1', subtitle: 'Description 1', Icon: <Polyline points="10,100 30,90 50,80 70,100 90,120 110,110 130,90 150,100 170,120 190,100" stroke="#3290ff" strokeWidth={4} /> },
  //         { id: '1', title: 'Mission 2', subtitle: 'Description 2', Icon: <Polyline points="10,200 30,180 50,170 70,160 90,150 110,130 130,140 150,160 170,150 190,100" stroke="#3290ff" strokeWidth={4} /> },
  //         { id: '2', title: 'Mission 3', subtitle: 'Description 3', Icon: <Polyline points="10,50 30,40 50,30 70,50 90,70 110,60 130,50 150,60 170,80 190,50" stroke="#3290ff" strokeWidth={4} /> },
  //     ];
  //     setMissions(initialMissions);
  //     setFilteredMissions(initialMissions);
  // }, []);

  async function addMission() {
    // 'http://localhost/API/Save/Mission'
    const url = "https://" + backendURI + "/Save/Mission"; // Replace with your actual endpoint
    let newMissionID = "Mission " + Math.floor(Math.random() * 99999999).toString();
    // let newMission = {
    //     [newMissionID]: {
    //         missionName: newMissionID,
    // 	    missionType: "Mission Type",
    // 	    missionDescription: "This is a mission description. The description goes here",
    // 	    vehicleID: 999,
    //     }
    // }
    let newMission = {
      missionId: newMissionID,
      missionName: "New Mission",
      missionType: "Mission Type",
      missionDescription: "Mission Description",
      taskRepetitions: "1",
      vehicleID: "Dusty",
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Specify that the content type is JSON
        },
        body: JSON.stringify({
          missionObject: newMission,
          currentTopicString: currentTopicString + "Missions/" + newMissionID,
        }), // Convert the pointsArray to JSON
      });

      // Check if the response is successful
      if (!response.ok) {
        let errorMsg = `Failed to save mission (${response.status})`;
        try {
          const errorBody = await response.json();
          if (errorBody?.message) errorMsg = errorBody.message;
        } catch {}
        throw new Error(errorMsg);
      }

      const data = await response.json(); // Parse the JSON response
      // console.log('Points saved successfully:', data);
    } catch (error) {
      enqueueToaster((error as Error)?.message || "Failed to save mission", "Error", {
        variant: "error",
      });
    }
  }

  const handleAddMission = () => {
    // Logic to add a new mission can be implemented here
    // console.log('Add mission functionality here');
    // let newMissionID = "Mission " + Math.floor(Math.random() * 99999999).toString();
    // let newMission = {
    //     [newMissionID]: {
    //         missionName: newMissionID,
    // 	    missionType: "Mission Type",
    // 	    missionDescription: "This is a mission description. The description goes here",
    // 	    vehicleID: 999,
    //     }
    // }
    // setMissionsObjects({
    //     ...missionsObjects,
    //     newMission
    // })
    addMission();
  };

  const handleImportMission = async () => {
    if (importFiles.length === 0) {
      enqueueToaster("Please select a file to import", "Error", { variant: "error" });
      return;
    }

    setIsImporting(true);

    try {
      const file = importFiles[0];
      const fileContent = await file.text();
      let parsedData = JSON.parse(fileContent);

      // Handle case where data might be wrapped in an extra object with mission ID as key
      let missionData = parsedData;

      // Check if the data has a nested structure with mission ID as key
      const keys = Object.keys(parsedData);
      if (
        keys.length > 0 &&
        keys.some((key) => key.startsWith("Mission")) &&
        typeof parsedData[keys[0]] === "object" &&
        parsedData[keys[0]].missionId
      ) {
        const nestedKey = keys.find((key) => key.startsWith("Mission"));
        if (nestedKey) {
          missionData = parsedData[nestedKey];
        }
      }

      // Validate the mission data structure
      if (!missionData.missionName || !Array.isArray(missionData.tasks)) {
        enqueueToaster("Invalid mission file format", "Error", { variant: "error" });
        return;
      }

      // Sanitize tasks: strip each task to only title, action, and the relevant properties field.
      const sanitizedTasks = (missionData.tasks as any[]).map((task: any) => {
        if (task.action === "Navigate") {
          return {
            title: task.title,
            action: "Navigate",
            properties: { uuid: task.properties?.uuid },
          };
        } else if (task.action === "Command") {
          const props: any = { workType: task.properties?.workType };
          if (
            task.properties?.workType === "GetLoaded" &&
            task.properties?.loadSide !== undefined
          ) {
            props.loadSide = task.properties.loadSide;
          }
          return {
            title: task.title,
            action: "Command",
            properties: props,
          };
        }
        return {
          title: task.title,
          action: task.action,
          properties: task.properties,
        };
      });

      missionData = { ...missionData, tasks: sanitizedTasks };

      // Validate UUIDs locally against current map data
      const navigateTasks = sanitizedTasks.filter((t: any) => t.action === "Navigate");
      const warnings: Array<{ taskIndex: number; uuid: string; message: string }> = [];

      sanitizedTasks.forEach((task: any, index: number) => {
        if (task.action === "Navigate") {
          const uuid = task.properties?.uuid;
          if (!uuid) {
            warnings.push({ taskIndex: index, uuid: "", message: "No destination UUID" });
          } else if (orgDataSession && Object.keys(orgDataSession).length > 0) {
            const found = findAttributeByUUID(orgDataSession, uuid);
            if (!found) {
              warnings.push({ taskIndex: index, uuid, message: "UUID not found on current map" });
            }
          } else {
            warnings.push({
              taskIndex: index,
              uuid,
              message: "Could not verify UUID — no map data loaded",
            });
          }
        }
      });

      if (warnings.length > 0) {
        setPendingImportData({ missionData, currentTopicString });
        setImportWarnings(warnings);
        setTotalNavigateTasks(navigateTasks.length);
        setShowConfirmDialog(true);
        setShowImportDialog(false);
        return;
      }

      const url = `https://${backendURI}/Save/ImportMission`;
      const importBody = { missionData: missionData, currentTopicString: currentTopicString };
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(importBody),
      });

      if (response.status === 401) {
        enqueueToaster("Session expired — please log in again", "Session Expired", {
          variant: "error",
        });
        setTimeout(() => window.location.assign("/API/auth/login"), 1500);
        return;
      }

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const result = await response.json();

      if (result.duplicate) {
        setDuplicateImportData(importBody);
        setDuplicateMissionName(result.missionName || missionData.missionName || "this mission");
        setShowImportDialog(false);
        setShowDuplicateDialog(true);
      } else if (result.success) {
        enqueueToaster("Mission imported successfully", "", { variant: "success" });
        setShowImportDialog(false);
        setImportFiles([]);
      } else {
        enqueueToaster(result.message || "Error importing mission", "Error", { variant: "error" });
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        enqueueToaster("Invalid JSON file — could not parse mission data", "Error", {
          variant: "error",
        });
      } else {
        enqueueToaster("Network error — could not import mission", "Error", { variant: "error" });
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingImportData) return;
    setShowConfirmDialog(false);
    try {
      const url = `https://${backendURI}/Save/ImportMission`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pendingImportData),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();
      if (result.duplicate) {
        setDuplicateImportData(pendingImportData);
        setDuplicateMissionName(
          result.missionName || pendingImportData?.missionData?.missionName || "this mission",
        );
        setShowDuplicateDialog(true);
      } else if (result.success) {
        enqueueToaster("Mission imported successfully", "", { variant: "success" });
        setImportFiles([]);
      } else {
        enqueueToaster(result.message || "Error importing mission", "Error", { variant: "error" });
      }
    } catch {
      enqueueToaster("Network error — could not import mission", "Error", { variant: "error" });
    } finally {
      setPendingImportData(null);
      setImportWarnings([]);
      setTotalNavigateTasks(0);
    }
  };

  const handleConfirmOverwrite = async () => {
    if (!duplicateImportData) return;
    setShowDuplicateDialog(false);
    try {
      const url = `https://${backendURI}/Save/ImportMission`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...duplicateImportData, overwrite: true }),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();
      if (result.success) {
        enqueueToaster("Mission imported successfully", "", { variant: "success" });
        setImportFiles([]);
      } else {
        enqueueToaster(result.message || "Error importing mission", "Error", { variant: "error" });
      }
    } catch {
      enqueueToaster("Network error — could not import mission", "Error", { variant: "error" });
    } finally {
      setDuplicateImportData(null);
      setDuplicateMissionName("");
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    setFilteredMissions(
      missions.filter((mission) => mission.title.toLowerCase().includes(term.toLowerCase())),
    );
  };

  const handleItemSelect = useCallback(
    (item: Item, i: number) => {
      setActiveItem(i);
      setCurrent(item, i);
      // console.log("MISSION SELECTED!!!");
      setCurMission(item);
      setCurMissionObject(getMissionFromID(item.id));
      // setSelectedMission(item); // Set the selected mission
      // setShowMissionPanel(true); // Show the MissionPanel
    },
    [activeItem, orgDataSession],
  );

  async function addMissionFix() {
    let newMissionID = "Mission " + Math.floor(Math.random() * 99999999).toString();
    let newMission = {
      missionId: newMissionID,
      missionName: "New Mission",
      missionType: "Mission Type",
      missionDescription: "Mission Description",
      vehicleID: "Dusty",
      taskRepetitions: "1",
    };
    getMissionItem2(newMission, newMissionID);
    // setActiveItem(0);
    setCurrent(getMissionItem2(newMission, newMissionID), 0);
    setCurMission(getMissionItem2(newMission, newMissionID));
    setCurMissionObject(newMission);
  }

  const handleCloseMissionPanel = () => {
    setShowMissionPanel(false); // Close the MissionPanel
    setSelectedMission(null); // Reset selected mission
  };

  const groups = useMemo(
    () => [
      {
        title: "Active (" + filteredMissions.length + ")",
        items: filteredMissions.map((item, i) => ({
          ...item,
          active: i === activeItem,
          onClick: () => handleItemSelect(item, i),
          secondaryAction: (
            <div>
              <IconButton
                className="chevronButton"
                icon={<IconChevronRight size={20} />}
                variant="borderless"
              />
            </div>
          ),
          extraContent: <div style={{ height: "58px" }} />,
        })),
      },
    ],
    [activeItem, filteredMissions, handleItemSelect],
  );

  const importFooterActions: FooterAction[] = [
    {
      title: "Cancel",
      variant: "secondary",
      onClick: (): void => {
        setShowImportDialog(false);
        setImportFiles([]);
      },
    },
    {
      title: "Import",
      variant: "primary",
      disabled: importFiles.length === 0 || isImporting,
      loading: isImporting,
      onClick: async (): Promise<void> => {
        await handleImportMission();
      },
    },
  ];

  return (
    <div className="missionsSubPanel">
      <div className="SearchAndAdd">
        <SearchField
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search Missions"
          style={{ flex: 1, height: "32px" }}
        />
        {hasPermission(role, "MISSION_CREATION") && (
          <IconButton
            icon={<IconUpload />}
            onClick={() => setShowImportDialog(true)}
            title="Import Mission"
            style={{ width: "32px", height: "32px" }}
          />
        )}
        {hasPermission(role, "MISSION_CREATION") && (
          <IconButton
            icon={<IconAdd />}
            onClick={addMissionFix}
            title="Add Mission"
            style={{ width: "32px", height: "32px" }}
          />
        )}
      </div>
      <div className="missionsList">
        <GroupedPanelList groups={groups} />
      </div>

      <Dialog
        open={showImportDialog}
        closeHandler={() => {
          setShowImportDialog(false);
          setImportFiles([]);
        }}
        title="Import Mission"
        width="sm"
        footerActions={importFooterActions}
      >
        <FileUploadField
          acceptableFileTypes={[".json"]}
          helperText="Only one file can be uploaded at a time."
          slotProps={{ fileInput: { multiple: false } as any }}
          files={importFiles}
          onChange={(newFiles: File[]) => {
            // Enforce single-file selection even when files are dropped.
            setImportFiles(newFiles.slice(0, 1));
          }}
        />
      </Dialog>

      <Dialog
        open={showConfirmDialog}
        closeHandler={() => {
          setShowConfirmDialog(false);
          setPendingImportData(null);
          setImportWarnings([]);
          setTotalNavigateTasks(0);
        }}
        title="Import Mission"
        width="sm"
        footerActions={[
          {
            title: "No",
            variant: "secondary",
            onClick: (): void => {
              setShowConfirmDialog(false);
              setPendingImportData(null);
              setImportWarnings([]);
              setTotalNavigateTasks(0);
            },
          },
          {
            title: "Yes",
            variant: "primary",
            onClick: async (): Promise<void> => {
              await handleConfirmImport();
            },
          },
        ]}
      >
        <p
          style={{
            marginBottom: "8px",
            backgroundColor: "#fff3cd",
            padding: "8px 12px",
            borderRadius: "4px",
          }}
        >
          {importWarnings.length} of {totalNavigateTasks} Navigate destinations could not be matched
          with the current map
        </p>
        <p style={{ marginBottom: "8px" }}>
          Unmatched destinations will be skipped during mission execution and preview.
        </p>
        <p style={{ margin: 0 }}>Do you still want to import this mission?</p>
      </Dialog>

      <Dialog
        open={showDuplicateDialog}
        closeHandler={() => {
          setShowDuplicateDialog(false);
          setDuplicateImportData(null);
          setDuplicateMissionName("");
        }}
        title="Mission Already Exists"
        width="sm"
        footerActions={[
          {
            title: "Cancel",
            variant: "secondary",
            onClick: (): void => {
              setShowDuplicateDialog(false);
              setDuplicateImportData(null);
              setDuplicateMissionName("");
            },
          },
          {
            title: "Overwrite",
            variant: "primary",
            onClick: async (): Promise<void> => {
              await handleConfirmOverwrite();
            },
          },
        ]}
      >
        <p style={{ marginBottom: "8px" }}>
          A mission named <strong>{duplicateMissionName}</strong> already exists.
        </p>
        <p style={{ margin: 0 }}>
          Do you want to overwrite the existing mission, or cancel the import?
        </p>
      </Dialog>
    </div>
  );
}

export default MissionsSubPanel;
